import { __ } from '../../utils';
import {
  NotificationsNoneOutlined,
  OutlinedFlag,
  StarBorder,
  ThumbsUpDownOutlined,
  TuneOutlined,
} from '@mui/icons-material';
import React from 'react';

/**
 * Sekcie profilu. Poradie je záväzné - je to poradie v ľavom páse aj
 * v mobilných záložkách.
 *
 * Kľúč sekcie je zároveň segment adresy (`/profil/podnety`), takže je po
 * slovensky ako každá iná routa v appke. Sekcia **nie je** query parameter:
 * profil je od redizajnu samostatná obrazovka so šiestimi podstránkami, nie
 * jedna stránka so záložkami, a adresa na konkrétnu z nich sa dá poslať.
 */
export type ProfileSectionKey =
  | 'podnety'
  | 'body'
  | 'zetony'
  | 'novinky'
  | 'notifikacie'
  | 'nastavenia';

export type ProfileSection = {
  key: ProfileSectionKey;
  /** menovka v ľavom páse a v mobilných záložkách */
  label: string;
  /** menovka tabu, keď sa líši od položky pásu (Notifikácie → tab Nastavenia) */
  tabLabel?: string;
  /** titul obrazovky a posledný článok cesty */
  title: string;
  icon: React.ReactNode;
  /**
   * Sekcia nie je samostatnou položkou pásu, ale tabom inej. Adresu si
   * nechá (odkazy z rozposlaných notifikácií vedú na `/profil/notifikacie`),
   * v páse a v mobilných záložkách svieti jej rodič.
   */
  tabOf?: ProfileSectionKey;
};

export const PROFILE_SECTIONS: ProfileSection[] = [
  {
    key: 'podnety',
    label: __('Moje podnety'),
    title: __('Moje podnety'),
    icon: <OutlinedFlag />,
  },
  {
    key: 'body',
    label: __('Body'),
    title: __('História bodov'),
    icon: <StarBorder />,
  },
  {
    key: 'zetony',
    label: __('Hlasovacie žetóny'),
    title: __('Použitie žetónov'),
    icon: <ThumbsUpDownOutlined />,
  },
  {
    key: 'novinky',
    label: __('Preferencie noviniek'),
    title: __('Preferencie noviniek'),
    icon: <TuneOutlined />,
  },
  {
    key: 'nastavenia',
    label: __('Notifikácie'),
    tabLabel: __('Nastavenia'),
    title: __('Nastavenia notifikácií'),
    icon: <NotificationsNoneOutlined />,
  },
  {
    key: 'notifikacie',
    label: __('História'),
    title: __('História notifikácií'),
    icon: <NotificationsNoneOutlined />,
    tabOf: 'nastavenia',
  },
];

/**
 * Položky ľavého pásu a mobilných záložiek - bez sekcií, ktoré sú tabom
 * inej. História notifikácií je od októbra 2026 tab v Nastaveniach: obe
 * hovoria o tom istom a vedľa seba v páse pôsobili ako dve rôzne veci.
 */
export const RAIL_SECTIONS = PROFILE_SECTIONS.filter(
  (section) => !section.tabOf,
);

/** Položka pásu, ktorá pri tejto sekcii svieti. */
export const railSectionKey = (section: ProfileSection): ProfileSectionKey =>
  section.tabOf ?? section.key;

/** Taby v rámci položky pásu - sekcia sama a tie, ktoré sú jej tabom. */
export const sectionTabs = (key: ProfileSectionKey): ProfileSection[] =>
  PROFILE_SECTIONS.filter((s) => s.key === key || s.tabOf === key);

/**
 * Predvolená sekcia. Sú to **moje podnety**: z hlavného menu zmizli a profil je
 * odteraz jediná cesta k nim, takže je to prvé, čo má občan po vstupe vidieť.
 */
export const DEFAULT_PROFILE_SECTION: ProfileSectionKey = 'podnety';

export const profilePath = (key: ProfileSectionKey) => `/profil/${key}`;

export const profileSection = (key?: string) =>
  PROFILE_SECTIONS.find((section) => section.key === key);

/**
 * Profil mal sekcie v `?tab=` a na tie adresy ukazujú odkazy v už rozposlaných
 * notifikáciách. Prekladajú sa v [[ProfileRedirect]]; nemažte ich bez toho, aby
 * ste vedeli, že najstaršie notifikácie sú už neaktuálne.
 */
export const LEGACY_TAB_SECTIONS: Record<string, ProfileSectionKey> = {
  body: 'body',
  tokens: 'zetony',
  preferences: 'novinky',
  notifications: 'notifikacie',
  'notifications-settings': 'nastavenia',
};
